import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { kMaxSearchPaginationPages, kSearchPageLimit, kSearchPaginationRequestDelayMs, kUserFilesSearchApiBasePath } from '../constants'
import { FolderSearchResponse, FolderSearchResponseSchema, FolderSearchResult } from '../schemas'
import { isAbortError } from '@/util/errors/is-abort-error'

export type MatterFolderSearchResult = {
  /** Every matching folder fetched, in backend order (prefix matches first, then by name). */
  folders: FolderSearchResult[]
  /** Total matches the backend found, which can exceed `folders.length` when truncated. */
  totalCount: number
  /** True when the page cap stopped pagination before the backend ran out of pages. */
  truncated: boolean
}

async function searchMatterFoldersPage(params: {
  cursor: string | null
  limit: number
  matterId: string
  pageIndex: number
  query: string
  sentryTransactionId: string
  signal?: AbortSignal
  token: string
}): Promise<FolderSearchResponse> {
  const searchParams = new URLSearchParams({
    query: params.query,
    limit: String(params.limit),
  })
  if (params.cursor) {
    searchParams.set('cursor', params.cursor)
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesSearchApiBasePath}/${encodeURIComponent(params.matterId)}/folders/search?${searchParams.toString()}`
  const response = await fetch(url, {
    method: 'GET',
    signal: params.signal,
    headers: {
      Authorization: `Bearer ${params.token}`,
      'X-Transaction-Id': params.sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)

    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          matterId: params.matterId,
          pageIndex: params.pageIndex,
          queryLength: params.query.length,
          responseData: errorData,
          parseError: parseError ? formatUnknownError(parseError) : null,
        },
      })
    })

    throw new ApiError(response.status, detail || `Failed to search folders: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder search response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          matterId: params.matterId,
          pageIndex: params.pageIndex,
          queryLength: params.query.length,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder search response JSON')
  }

  const parsed = FolderSearchResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: params.sentryTransactionId })
      Sentry.captureException(new Error('Folder search response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          cursor: params.cursor ?? null,
          matterId: params.matterId,
          pageIndex: params.pageIndex,
          queryLength: params.query.length,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid folder search response shape', parsed.error.issues)
  }

  return parsed.data
}

/**
 * Search a matter's folders by name (case-insensitive substring, root and deleted folders excluded).
 *
 * Exhausts the backend cursor the way the drive's list fetchers do, so callers get one complete
 * result set to sort and select over. A query broad enough to pass the page cap returns the pages
 * fetched so far with `truncated: true` rather than throwing — the user still sees results.
 *
 * @param matterId - The matter to search within (UUID)
 * @param options.query - Non-empty search term
 * @param options.signal - Optional signal used to abort a superseded query
 * @param options.limit - Page size; defaults to the backend maximum
 * @param options.maxPages - Pages to fetch before giving up and returning `truncated`; the
 *   suggestions dropdown passes 1 because it renders only the first handful of matches
 * @throws Error when user is not authenticated
 * @throws ApiError when the search request fails
 */
export async function searchMatterFolders(
  matterId: string,
  options: { query: string; signal?: AbortSignal; limit?: number; maxPages?: number }
): Promise<MatterFolderSearchResult> {
  const sentryTransactionId = nanoid()
  const { query, signal } = options
  const limit = options.limit ?? kSearchPageLimit
  const maxPages = options.maxPages ?? kMaxSearchPaginationPages

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const folders: FolderSearchResult[] = []
    let totalCount = 0
    let nextCursor: string | null = null
    let pageIndex = 1
    let truncated = false
    const seenCursors = new Set<string>()

    do {
      if (pageIndex > maxPages) {
        truncated = true
        break
      }

      if (nextCursor) {
        if (seenCursors.has(nextCursor)) {
          throw new Error('Encountered a repeated pagination cursor while searching folders')
        }

        seenCursors.add(nextCursor)
      }

      const page = await searchMatterFoldersPage({
        cursor: nextCursor,
        limit,
        matterId,
        pageIndex,
        query,
        sentryTransactionId,
        signal,
        token,
      })

      folders.push(...page.folders)
      totalCount = page.total_count
      nextCursor = page.next_cursor
      pageIndex += 1

      // Only pause when another page will actually be fetched — on the iteration that is about to
      // stop at the page cap the delay would be pure latency, which the typing path pays per query.
      if (nextCursor && pageIndex <= maxPages) {
        await new Promise((resolve) => setTimeout(resolve, kSearchPaginationRequestDelayMs))
      }
    } while (nextCursor)

    return { folders, totalCount, truncated }
  } catch (error) {
    if (isAbortError(error)) {
      throw error
    }

    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            matterId,
            queryLength: query.length,
          },
        })
      })
    }
    throw error
  }
}
